/**
 * Article share button + clipboard fallback.
 *
 * Wires the share affordance with native-first behavior:
 *   - Mobile / supporting browsers: opens the native OS share sheet via
 *     `navigator.share()`.
 *   - Desktop / non-supporting: toggles the inline share dropdown
 *     (`.xot-share-menu.open`) and tracks `aria-expanded` for a11y.
 *   - Copy-link button inside the dropdown writes to the clipboard,
 *     flashes a 2.5s confirmation, and falls back to `prompt()` when
 *     the Clipboard API is unavailable (rare, but covers permission-
 *     denied + insecure-context cases).
 *
 * Build path:
 *   src   assets/js/src/single-article/share.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/share.js  (attaches window.XOT.article.share)
 *
 * Test path:
 *   src   assets/js/src/single-article/share.ts
 *   ↓ vitest (jsdom env, navigator.share + clipboard mocked)
 *   test  tests/js/single-article-share.test.ts
 *
 * @since Phase 2 — JS modernization, bite 7
 */

const CONFIRM_MS  = 2500;
const ICON_HTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>Copy Link';

export interface ShareElements {
    btn:    HTMLElement | null;
    menu:   HTMLElement | null;
    copy:   HTMLElement | null;
}

/** Lookup the share DOM scaffold. Returns nulls when absent — caller decides. */
export function queryShareElements(): ShareElements {
    return {
        btn:  document.getElementById('xot-share-btn'),
        menu: document.getElementById('xot-share-menu'),
        copy: document.getElementById('xot-copy-link-btn'),
    };
}

/** Read the share title/url, falling back to document.title + location.href. */
export function readShareTarget(btn: HTMLElement): { title: string; url: string } {
    return {
        title: btn.dataset.title || document.title,
        url:   btn.dataset.url   || (typeof location !== 'undefined' ? location.href : ''),
    };
}

/**
 * Wire up the share button + clipboard + outside-click close. Returns a
 * disposer that detaches every listener.
 *
 * Idempotent against missing scaffold — if `#xot-share-btn` isn't on
 * the page, init returns a no-op disposer.
 */
export function initShare(): () => void {
    if (typeof document === 'undefined') return () => {};
    const { btn, menu, copy } = queryShareElements();
    if (!btn) return () => {};

    const { title, url } = readShareTarget(btn);

    function onShareClick(e: Event): void {
        e.stopPropagation();
        const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
        if (typeof nav.share === 'function') {
            nav.share({ title, url }).catch(() => { /* user cancelled / not allowed — silent */ });
            return;
        }
        // Desktop fallback — toggle dropdown
        menu?.classList.toggle('open');
        btn!.setAttribute('aria-expanded', menu?.classList.contains('open') ? 'true' : 'false');
    }

    function onDocumentClick(e: Event): void {
        const target = e.target as Element | null;
        if (!target?.closest('.xot-share-wrap')) {
            menu?.classList.remove('open');
            btn!.setAttribute('aria-expanded', 'false');
            document.getElementById('xot-share-backdrop')?.remove();
        }
    }

    function onCopyClick(): void {
        if (!copy) return;
        const nav = navigator as Navigator & { clipboard?: { writeText: (s: string) => Promise<void> } };
        if (nav.clipboard?.writeText) {
            nav.clipboard.writeText(url).then(() => {
                copy.textContent = '✓ Copied!';
                window.setTimeout(() => { copy.innerHTML = ICON_HTML; }, CONFIRM_MS);
            }).catch(() => {
                // Permission denied / insecure context — manual copy path
                window.prompt('Copy this link:', url);
            });
            return;
        }
        // No Clipboard API at all (very old browser) — straight to prompt
        window.prompt('Copy this link:', url);
    }

    btn.addEventListener('click', onShareClick);
    document.addEventListener('click', onDocumentClick);
    copy?.addEventListener('click', onCopyClick);

    return function dispose(): void {
        btn.removeEventListener('click', onShareClick);
        document.removeEventListener('click', onDocumentClick);
        copy?.removeEventListener('click', onCopyClick);
    };
}

/* ── window.XOT.article.share attachment (legacy IIFE consumers) ───── */
interface XotArticleShare {
    init:               typeof initShare;
    queryShareElements: typeof queryShareElements;
    readShareTarget:    typeof readShareTarget;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:      unknown;
                highlight?: unknown;
                progress?:  unknown;
                share?:     XotArticleShare;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.share = {
        init:               initShare,
        queryShareElements,
        readShareTarget,
    };
    xot.article = article;
    window.XOT  = xot;
}
